import i18n from '../i18n';
import { _dataProvider } from '../dataProvider/DataProvider';

import type { IGroupDetailData, IGroupItem } from '../dataProvider/IGroups';

type IMessageData = {
  type?: string;
  payload?: any;
};

const getDebug = () => {
  return {
    isDebug: new URLSearchParams(location.search).has('debug'),
    // debugMeetingOrigin: 'https://local.fishmeet.online:3000'
    debugMeetingOrigin: 'https://127.0.0.1:8080'
  };
};

type IMeetingBaseParams = {
  groupId: IGroupItem['groupId'];
};

type IOpenMeetingParams = IMeetingBaseParams & {
  enablePreBreakout?: boolean;
};
export const getMeetingUrl = async (params: IOpenMeetingParams) => {
  const { groupId, enablePreBreakout } = params;

  try {
    let { url } = (await _dataProvider.getGroupMeetLink(groupId)) ?? {};
    if (!url) {
      return undefined;
    }

    const { origin, pathname, search, hash } = new URL(url);
    const iUsp = new URLSearchParams(search);
    if (enablePreBreakout) {
      iUsp.set('pre-breakout', '1');
    }

    if (i18n.language === 'eng') {
      iUsp.set('zhCN', 'en');
    }

    url = `${origin}${pathname}?${iUsp.toString()}${hash}`;

    const features = [
      'height=' + screen.height,
      'width=' + screen.width,
      'fullscreen=yes', // only works in IE, but here for completeness
      'toolbar=0,location=0,menubar=0,status=0,location=0,titlebar=0,top=100,left=0'
    ].join(',');

    const { isDebug, debugMeetingOrigin } = getDebug();
    if (isDebug) {
      url = `${debugMeetingOrigin}${pathname}?${iUsp.toString()}${hash}`;
    }

    console.log('[Meeting] getUrl: ', { url, features });
    return {
      url,
      features
    };
  } catch (err) {
    console.log('getMeetingUrl error:', err);
  }
};

export const openMeeting = async (params: IOpenMeetingParams) => {
  const { groupId, enablePreBreakout } = params;

  // if meeting window is existed and opened, focus it
  if (existMeetingWindow()) {
    if (_meetingGroupId === groupId) {
      _meetingWindow!.focus();
      return;
    }

    const cont = confirm(i18n.t('Meeting.SwitchConfirm'));

    if (!cont) {
      _meetingWindow!.focus();
      return;
    }
  }

  const { url, features = '' } =
    (await getMeetingUrl({
      groupId,
      enablePreBreakout
    })) ?? {};

  if (!url) {
    alert(i18n.t('Meeting.NetworkFailed'));
    return;
  }

  openMeetingWindow({ url, features, groupId });
};

let _meetingWindow: Window | null;
let _meetingGroupId: IGroupItem['groupId'] | null;
let _meetingOrigin: string | null = null;

type IOpenMeetingWindowParams = {
  url: string;
  features: string;
  groupId: IGroupItem['groupId'];
};

type IExistMeetingWindow = Pick<IOpenMeetingWindowParams, 'groupId'>;
export const existMeetingWindow = (params?: IExistMeetingWindow) => {
  const { groupId } = params ?? {};
  if (groupId != null) {
    return Boolean(_meetingWindow && !_meetingWindow.closed && _meetingGroupId === groupId);
  }

  return Boolean(_meetingWindow && !_meetingWindow.closed);
};

export const openMeetingWindow = (params: IOpenMeetingWindowParams) => {
  const { url, features = '', groupId } = params ?? {};

  _meetingWindow = window.open(url, 'idigestMeeting', features);
  if (_meetingWindow) {
    _meetingWindow.focus();
    _meetingGroupId = groupId;
    try {
      _meetingOrigin = new URL(url).origin;
    } catch {
      _meetingOrigin = null;
    }
  }

  return _meetingWindow;
};

const isTrustedMeetingMessage = (event: MessageEvent<IMessageData>) => {
  if (!_meetingWindow || _meetingWindow.closed || event.source !== _meetingWindow) {
    return false;
  }
  if (_meetingOrigin != null && event.origin !== _meetingOrigin) {
    return false;
  }
  return true;
};

type ISendBreakoutConfig = IMeetingBaseParams & {
  onSuccess?: () => void;
};
export const sendBreakoutConfig = (params: ISendBreakoutConfig) => {
  const { groupId, onSuccess } = params;

  let messageListener: ((event: MessageEvent<IMessageData>) => void) | undefined = async (
    event: MessageEvent<IMessageData>
  ) => {
    const { type: msgType } = event.data ?? {};

    if (msgType === 'Request-MeetingBreakoutRoomParams') {
      if (!isTrustedMeetingMessage(event)) {
        return;
      }
      try {
        const { meetingCode, meetingData } = ((await _dataProvider.getGroupInfo(groupId)) as IGroupDetailData) ?? {};
        event.source?.postMessage(
          {
            type: 'Response-MeetingBreakoutRoomParams',
            payload: { groupId, meetingCode, meetingData }
          },
          { targetOrigin: event.origin }
        );
      } catch (err) {
        console.log('[sendBreakoutConfig] getMeetingData error:', err);
        return;
      }

      console.log('IDigest: Response-MeetingBreakoutRoomParams', event.data);
    }

    if (msgType === 'Received-MeetingBreakoutRoomParams') {
      if (!isTrustedMeetingMessage(event)) {
        return;
      }
      clean();
      onSuccess?.();
      console.log('IDigest: Received-MeetingBreakoutRoomParams');
    }
  };

  window.addEventListener('message', messageListener);

  const clean = () => {
    if (!messageListener) {
      return;
    }
    window.removeEventListener('message', messageListener);
    messageListener = undefined;
  };

  return { clean };
};
